import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid as Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { startScoreTypes } from "../../constants/enrollment";
import { useEffect, useState } from "react";
import { Cancel, Check, CloudUpload } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import ImageDialog from "../../actprep-ui/components/ImageDialog";
import { actSubjects } from "../../actprep-ui/constants/program";
import { capitalize } from "../../actprep-ui/utils/formatting";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import api from "../../actprep-ui/utils/api";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { LoadingButton } from "@mui/lab";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function StudentStartScoreEditor({
  studentId,
  onClose,
  onSubmit,
}) {
  const [fullTest, setFullTest] = useState(null);
  const { data: subjectTestData } = useGet(apiUrls.admin.subjectTests(), {
    enabled: !!fullTest,
    params: {
      search_params: {
        full_test_id: fullTest?.uuid,
      },
    },
    staleTime: 0,
  });
  const {
    data: studentData,
    isLoading,
    refetch: refetchStudent,
  } = useGet(apiUrls.admin.studentProfiles(studentId), {
    params: {
      values: [
        "first_name",
        "last_name",
        "start_scores_image",
        "start_scores_type",
        "start_composite",
        "start_english",
        "start_math",
        "start_reading",
        "start_science",
      ],
    },
  });

  const [formData, setFormData] = useState({});
  // New state for the uploaded file and its preview URL
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [savingInProgress, setSavingInProgress] = useState(false);
  const viewImageDisclosure = useDisclosure();

  useEffect(() => {
    if (studentData && !Object.keys(formData).length) {
      setFormData(studentData.data);
    }
  }, [studentData, formData]);

  useEffect(() => {
    if (formData.start_scores_type != startScoreTypes.MOCK_ACT && fullTest) {
      setFullTest(null);
    }
  }, [formData.start_scores_type, fullTest]);

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleUpdateFormData = (key, val) => {
    setFormData((currFormData) => ({ ...currFormData, [key]: val }));
  };

  // Handle file selection and create a preview URL
  const handleFileUpload = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
      handleUpdateFormData("start_scores_image", file);
    }
  };

  // Use formData for the score type so that we reflect any changes
  const scoreType = formData.start_scores_type;
  const student = studentData?.data;

  const subjectTestScoreMap = Object.fromEntries(
    actSubjects.map((subject) => {
      const subjectTest = (subjectTestData?.data || []).filter(
        ({ subject: testSubject }) => testSubject == subject
      )[0];
      if (subjectTest) {
        return [subject, subjectTest.score];
      }
      return [subject, ""];
    })
  );
  subjectTestScoreMap["composite"] = fullTest?.data?.score ?? "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSavingInProgress(true);
    const finalFormData = new FormData();
    if (formData.start_scores_image) {
      finalFormData.append("start_scores_image", formData.start_scores_image);
    }
    if (fullTest) {
      for (let [subject, score] of Object.entries(subjectTestScoreMap)) {
        finalFormData.append(`start_${subject}`, score);
      }
    }
    finalFormData.append("start_scores_type", formData.start_scores_type);
    for (let [key, val] of Object.entries(formData)) {
      if (finalFormData.get(key)) {
        continue;
      }
      finalFormData.append(key, val);
    }
    const response = await api.post(
      apiUrls.admin.startScores(studentId),
      finalFormData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    setSavingInProgress(false);
    if (response.status == 200) {
      showSuccessSnackbar("Scores Updated!");
      onSubmit(Object.fromEntries(finalFormData));
      refetchStudent();
      if (onClose) {
        onClose();
      }
    }
  };

  return (
    <Grid
      container
      sx={{ width: "100%" }}
      spacing={2}
      component={"form"}
      onSubmit={handleSubmit}
    >
      <Grid size={12}>
        <FormControl fullWidth variant="standard">
          <InputLabel>Scores Type</InputLabel>
          <Select
            value={formData.start_scores_type || ""}
            required
            onChange={(event) =>
              handleUpdateFormData("start_scores_type", event.target.value)
            }
          >
            {Object.values(startScoreTypes).map((startScoreType, i) => (
              <MenuItem key={i} value={startScoreType}>
                {startScoreType}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {scoreType == startScoreTypes.MOCK_ACT && (
        <Grid size={12}>
          <ModelAutoComplete
            modelName={"FullTest"}
            label={"Full Test"}
            value={fullTest}
            setValue={setFullTest}
            maxSize={100}
            required
            includeFields={["score"]}
            defaultParams={{
              is_complete: true,
            }}
          />
        </Grid>
      )}
      {[
        startScoreTypes.OFFICIAL_ACT,
        startScoreTypes.PRE_ACT,
        startScoreTypes.OTHER,
      ].includes(scoreType) && (
        <Grid size={12}>
          <Typography
            variant="overline"
            fontWeight={"bold"}
            fontSize={"medium"}
          >
            Start Scores Image
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <Button
              component="label"
              variant="contained"
              startIcon={<CloudUpload />}
            >
              {selectedFile || student.start_scores_image
                ? "Change Photo"
                : "Upload Photo"}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
              />
            </Button>

            <Typography variant="body2" component={"div"}>
              {selectedFile
                ? `Selected file: ${selectedFile.name}`
                : student.start_scores_image
                ? "Photo already uploaded"
                : "No photo uploaded yet"}

              <Chip
                sx={{ ml: 1 }}
                label="View Image"
                onClick={viewImageDisclosure.onOpen}
                disabled={!(previewImage || student.start_scores_image)}
                size="small"
              />
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
        </Grid>
      )}
      <Grid size={12} container spacing={1}>
        <Grid size={12}>
          <Typography
            variant="overline"
            fontWeight={"bold"}
            fontSize={"medium"}
          >
            Start Scores
          </Typography>
        </Grid>
        {[...actSubjects, "composite"].map((subject, i) => (
          <Grid size={subject == "composite" ? 12 : 6} key={i}>
            <TextField
              fullWidth
              label={capitalize(subject)}
              disabled={!!fullTest && subjectTestScoreMap[subject] != ""}
              required
              type="number"
              onChange={(event) =>
                handleUpdateFormData(`start_${subject}`, event.target.value)
              }
              value={
                (fullTest
                  ? subjectTestScoreMap[subject] || formData[`start_${subject}`]
                  : formData[`start_${subject}`]) || ""
              }
            />
          </Grid>
        ))}
      </Grid>
      <Grid size={12} mt={3} display={"flex"} justifyContent={"flex-end"}>
        <ButtonGroup variant="contained">
          <LoadingButton
            startIcon={<Check />}
            type="submit"
            loading={savingInProgress}
          >
            Save
          </LoadingButton>
          {onClose && (
            <Button
              startIcon={<Cancel />}
              onClick={onClose}
              disabled={savingInProgress}
            >
              Cancel
            </Button>
          )}
        </ButtonGroup>
      </Grid>
      <ImageDialog
        src={previewImage || student.start_scores_image}
        {...viewImageDisclosure.getDisclosureProps()}
      />
    </Grid>
  );
}
